// src/pages/Admin/ReglementInscription/CreateReglementInscriptionModal.jsx
import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import { toast } from "react-toastify";
import { FaSave, FaTimes } from "react-icons/fa";
import Button from "../../../components/Button/Button";
import { createReglementInscription } from "../../../services/reglementinscriptionServices";
import { getAllAdherentsList } from "../../../services/adherentServices";
import { getSettings } from "../../../services/settingsServices";
import {
  getAllDossiersInscription,
  updateDossierInscription,
} from "../../../services/dossierServices";

/* Utils */
const fmtEUR = (n) =>
  new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 2,
  }).format(Number(n || 0));

function neighborsFromSeason(season) {
  const m = /^(\d{4})\s*-\s*(\d{4})$/.exec(season || "");
  if (!m) return [season].filter(Boolean);
  const y1 = parseInt(m[1], 10);
  const y2 = parseInt(m[2], 10);
  return [`${y1 - 1}-${y2 - 1}`, `${y1}-${y2}`, `${y1 + 1}-${y2 + 1}`];
}

/* Sélecteur adhérent avec recherche */
function AdherentPicker({ adherents, value, onSelect }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const selected = useMemo(
    () => adherents.find((a) => String(a.id) === String(value)) || null,
    [adherents, value],
  );

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return adherents.slice(0, 50);
    const res = adherents.filter((a) => {
      const txt = `${a.nom || ""} ${a.prenom || ""} ${
        a.email || ""
      }`.toLowerCase();
      return txt.includes(query);
    });
    return res.slice(0, 50);
  }, [adherents, q]);

  useEffect(() => {
    if (!open) setHighlight(0);
  }, [open]);

  const handleKeyDown = (e) => {
    if (!open && (e.key === "ArrowDown" || e.key === "Enter")) {
      setOpen(true);
      return;
    }
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const item = filtered[highlight];
      if (item) {
        onSelect(item);
        setQ("");
        setOpen(false);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const choose = (a) => {
    onSelect(a);
    setQ("");
    setOpen(false);
    inputRef.current?.blur();
  };

  return (
    <div className="relative">
      <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
        Adhérent <span className="text-red-600">*</span>
      </label>

      {/* Sélection actuelle */}
      {selected && (
        <div className="mb-2 rounded-lg ring-1 ring-slate-300 dark:ring-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm flex items-center justify-between">
          <span className="truncate">
            {selected.nom} {selected.prenom}{" "}
            <span className="opacity-60">#{selected.id}</span>
          </span>
          <button
            type="button"
            onClick={() => onSelect(null)}
            className="text-[12px] underline decoration-dotted opacity-80 hover:opacity-100"
            title="Changer d’adhérent"
          >
            changer
          </button>
        </div>
      )}

      {/* Champ recherche */}
      <input
        ref={inputRef}
        type="text"
        value={q}
        onFocus={() => setOpen(true)}
        onChange={(e) => setQ(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Rechercher (nom, prénom, email)…"
        className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
      />

      {/* Résultats */}
      {open && filtered.length > 0 && (
        <div
          ref={listRef}
          className="absolute z-20 mt-1 w-full max-h-64 overflow-auto rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-lg"
        >
          {filtered.map((a, idx) => (
            <button
              type="button"
              key={a.id}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => choose(a)}
              className={`w-full text-left px-3 py-2 text-sm transition ${
                idx === highlight
                  ? "bg-violet-50/80 dark:bg-slate-800/60"
                  : "hover:bg-slate-50 dark:hover:bg-slate-800/40"
              }`}
            >
              <div className="font-medium">
                {a.nom} {a.prenom} <span className="opacity-60">#{a.id}</span>
              </div>
              {a.email && (
                <div className="text-[12px] opacity-70">{a.email}</div>
              )}
            </button>
          ))}
          {filtered.length >= 50 && (
            <div className="px-3 py-2 text-[11px] opacity-70">
              Affichage limité à 50 résultats — affine la recherche.
            </div>
          )}
        </div>
      )}
      <input type="hidden" name="adherent_id" value={selected?.id || ""} />
    </div>
  );
}

const CreateReglementInscriptionModal = ({
  isModalOpen,
  setIsModalOpen, // compat
  fetchReglements,
  onClose,
}) => {
  const [adherents, setAdherents] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [activeSeason, setActiveSeason] = useState("");

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    adherent_id: "",
    saison: "",
    montant_lic_assurance: "",
    montant_adhesion: "",
    montant_cotisation: "",
    reduction_foyer: "",
    pass_sport: "",
    reduction_saison: "",
    type_reglement: "",
    nb_reglement: "1",
    observations: "",
    statut_reglement: "",
  });

  // Total live
  const liveTotal = useMemo(() => {
    const n = (v) => Number(String(v).replace(",", ".")) || 0;
    return (
      n(form.montant_lic_assurance) +
      n(form.montant_adhesion) +
      n(form.montant_cotisation) +
      n(form.reduction_foyer) +
      n(form.pass_sport) +
      n(form.reduction_saison)
    );
  }, [form]);

  const loadContext = useCallback(async () => {
    setLoading(true);
    try {
      const [ads, settings] = await Promise.all([
        getAllAdherentsList(),
        getSettings(),
      ]);
      setAdherents(Array.isArray(ads) ? ads : []);
      const active = settings?.[0]?.saison_active || "";
      setActiveSeason(active);
      setSeasons(neighborsFromSeason(active));
      // Pré-remplir la saison avec l’active si pas déjà saisie
      setForm((prev) => ({ ...prev, saison: active || prev.saison || "" }));
    } catch (e) {
      console.error(e);
      toast.error("Impossible de charger les adhérents / paramètres.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isModalOpen) return;
    // reset form à l’ouverture
    setForm({
      adherent_id: "",
      saison: "",
      montant_lic_assurance: "",
      montant_adhesion: "",
      montant_cotisation: "",
      reduction_foyer: "",
      pass_sport: "",
      reduction_saison: "",
      type_reglement: "",
      nb_reglement: "",
      observations: "",
      statut_reglement: "",
    });
    loadContext();
  }, [isModalOpen, loadContext]);

  const handleBasicChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  // adherent + statut requis
  const canSubmit = useMemo(() => {
    return (
      !loading &&
      !submitting &&
      String(form.adherent_id || "").trim() !== "" &&
      String(form.statut_reglement || "").trim() !== ""
    );
  }, [form, loading, submitting]);

  // 🔗 Synchronisation Dossier.validate_reglement après création
  const syncDossierValidateReglement = useCallback(
    async (adherentId, saison, statut) => {
      try {
        const validated =
          statut === "Réglé" || statut === "Partiellement réglé";

        // on récupère les dossiers et on cherche par (adherentId + saison)
        const dossiers = await getAllDossiersInscription();
        const arr = Array.isArray(dossiers) ? dossiers : [];

        // on tente sur `d.saison` OU `d.Adherent.saison_active`
        const target = arr.find((d) => {
          const aId = d?.inscription_adherent_id || d?.Adherent?.id;
          const s = d?.saison || d?.Adherent?.saison_active;
          return (
            String(aId) === String(adherentId) && String(s) === String(saison)
          );
        });

        if (!target) {
          console.warn(
            "[CreateReglement] Aucun dossier trouvé pour adherent=",
            adherentId,
            " saison=",
            saison,
          );
          return; // on ne bloque pas la création si pas de dossier
        }

        await updateDossierInscription(target.id, {
          validate_reglement: validated,
        });
      } catch (e) {
        console.error(
          "[CreateReglement] Erreur synchronisation dossier.validate_reglement:",
          e,
        );
        // on informe mais on ne bloque pas
        toast.error(
          "Règlement créé, mais la synchronisation du dossier a échoué.",
        );
      }
    },
    [],
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const numOrZero = (v) =>
        v === "" || v === null || v === undefined ? 0 : Number(v);

      const payload = {
        adherent_id: form.adherent_id,
        saison: form.saison || activeSeason || null,
        montant_lic_assurance: numOrZero(form.montant_lic_assurance),
        montant_adhesion: numOrZero(form.montant_adhesion),
        montant_cotisation: numOrZero(form.montant_cotisation),
        reduction_foyer: numOrZero(form.reduction_foyer),
        pass_sport: numOrZero(form.pass_sport),
        reduction_saison: numOrZero(form.reduction_saison),
        type_reglement: form.type_reglement || "",
        nb_reglement: (() => {
          const n = Number(form.nb_reglement);
          return Number.isFinite(n) && n > 0 ? n : 1;
        })(),
        observations: form.observations || "",
        statut_reglement: form.statut_reglement,
      };

      await createReglementInscription(payload);

      // ⬇️ Synchronisation auto du dossier
      await syncDossierValidateReglement(
        payload.adherent_id,
        payload.saison || activeSeason,
        payload.statut_reglement,
      );

      toast.success("Règlement créé avec succès.");
      fetchReglements?.();
      onClose?.();
    } catch (e) {
      console.error("Erreur création règlement", e);
      toast.error("Erreur lors de la création du règlement.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isModalOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* overlay */}
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />
      {/* conteneur */}
      <div className="relative w-full max-w-3xl overflow-hidden rounded-2xl shadow-2xl bg-white dark:bg-slate-900 ring-1 ring-slate-200/70 dark:ring-slate-700/70">
        {/* header */}
        <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4">
          <h3 className="text-white text-lg sm:text-xl font-bold text-center uppercase">
            Créer un règlement d’inscription
          </h3>
          <p className="text-white/90 text-center text-xs mt-1">
            Seuls l’adhérent et le statut sont obligatoires. La saison prend par
            défaut la saison active si vous ne la renseignez pas.
          </p>
        </div>

        {/* contenu */}
        <form onSubmit={handleSubmit} className="relative">
          <div className="p-5 sm:p-6 space-y-6 max-h:[80vh] max-h-[80vh] overflow-y-auto">
            {/* Bloc 1 : Identité & Saison */}
            <div className="rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 p-4 bg-white/70 dark:bg-slate-900/40">
              <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-3">
                Identité & Saison
              </h4>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <AdherentPicker
                  adherents={adherents}
                  value={form.adherent_id}
                  onSelect={(a) =>
                    setForm((p) => ({ ...p, adherent_id: a ? a.id : "" }))
                  }
                />

                <div>
                  <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                    Saison (optionnel)
                  </label>
                  <select
                    name="saison"
                    value={form.saison}
                    onChange={handleBasicChange}
                    disabled={loading}
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  >
                    <option value="">
                      {loading
                        ? "Chargement…"
                        : activeSeason
                          ? `— Saison active par défaut : ${activeSeason} —`
                          : "— Laisser vide si inconnu —"}
                    </option>
                    {seasons.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-[11px] text-slate-500">
                    Si vous laissez vide, la saison{" "}
                    {activeSeason ? (
                      <>
                        <span className="font-medium">{activeSeason}</span> sera
                        utilisée.
                      </>
                    ) : (
                      "ne sera pas renseignée."
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* Bloc 2 : Montants */}
            <div className="rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 p-4 bg-white/70 dark:bg-slate-900/40">
              <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-3">
                Montants
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Licence & Assurance (€)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    name="montant_lic_assurance"
                    value={form.montant_lic_assurance}
                    onChange={handleBasicChange}
                    placeholder="0,00"
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Adhésion (€)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    name="montant_adhesion"
                    value={form.montant_adhesion}
                    onChange={handleBasicChange}
                    placeholder="0,00"
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    Cotisation (€)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    name="montant_cotisation"
                    value={form.montant_cotisation}
                    onChange={handleBasicChange}
                    placeholder="0,00"
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Réduction foyer (€)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    name="reduction_foyer"
                    value={form.reduction_foyer}
                    onChange={handleBasicChange}
                    placeholder="ex. -20,00"
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Pass’Sport (€)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    name="pass_sport"
                    value={form.pass_sport}
                    onChange={handleBasicChange}
                    placeholder="ex. -70,00"
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Réduction saison (€)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    name="reduction_saison"
                    value={form.reduction_saison}
                    onChange={handleBasicChange}
                    placeholder="ex. -10,00"
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                  />
                </div>
              </div>

              <div className="mt-3 text-sm">
                Total provisoire :{" "}
                <span className="font-semibold">{fmtEUR(liveTotal)}</span>
              </div>
            </div>

            {/* Bloc 3 : Règlement */}
            <div className="rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 p-4 bg-white/70 dark:bg-slate-900/40">
              <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-3">
                Mode & Statut du règlement
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Type de règlement (optionnel)
                  </label>
                  <select
                    name="type_reglement"
                    value={form.type_reglement}
                    onChange={handleBasicChange}
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                  >
                    <option value="">— Sélectionner —</option>
                    <option value="En ligne">En ligne</option>
                    <option value="Chèque">Chèque</option>
                    <option value="Espèces">Espèces</option>
                    <option value="Virement">Virement</option>
                    <option value="CB">CB</option>
                    <option value="Autre">Autre</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    Nombre de règlements (optionnel)
                  </label>
                  <input
                    type="number"
                    name="nb_reglement"
                    min={1}
                    inputMode="numeric"
                    value={form.nb_reglement}
                    onChange={handleBasicChange}
                    placeholder="ex. 1, 2, 3…"
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    Statut <span className="text-red-600">*</span>
                  </label>
                  <select
                    name="statut_reglement"
                    value={form.statut_reglement}
                    onChange={handleBasicChange}
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                    required
                  >
                    <option value="">— Sélectionner —</option>
                    <option value="Réglé">Réglé</option>
                    <option value="Partiellement réglé">
                      Partiellement réglé
                    </option>
                    <option value="En cours">En cours</option>
                    <option value="Non réglé">Non réglé</option>
                    <option value="Problème">Problème</option>
                    <option value="Annulé">Annulé</option>
                  </select>
                </div>

                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium mb-1">
                    Observations / N° Pass’Sport (optionnel)
                  </label>
                  <textarea
                    name="observations"
                    value={form.observations}
                    onChange={handleBasicChange}
                    rows={3}
                    placeholder="Notes libres…"
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Barre d’actions sticky */}
          <div className="sticky bottom-0 -mx-5 sm:-mx-6 bg-white/90 dark:bg-slate-900/90 backdrop-blur border-t border-slate-200 dark:border-slate-800 px-5 sm:px-6 py-3 flex justify-end gap-2">
            <Button
              text="Annuler"
              icon={FaTimes}
              onClick={onClose}
              type="button"
              className="!bg-slate-100 !text-slate-800 hover:!bg-slate-200 dark:!bg-slate-800 dark:!text-slate-100 dark:hover:!bg-slate-700 !rounded-xl gap-2"
            />
            <Button
              text={submitting ? "Enregistrement…" : "Enregistrer"}
              icon={FaSave}
              type="submit"
              disabled={!canSubmit}
              className={`!rounded-xl gap-2 ${
                !canSubmit ? "opacity-60 pointer-events-none" : ""
              }`}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateReglementInscriptionModal;
